import { apiClient, ApiType } from '@shared/apiClient';
import { ApiPaths } from '@shared/apiClient/ApiPaths/ApiPaths';

const DYNAMIC_FORM_SUCCESS_STATUSES = [200, 202];
const ORCHESTRATION_INSTANCE_ID = 'orchestrationInstanceId';

export type DocumentFormData = Record<string, unknown>;

export type DynamicFormResponseItem = {
  id: string;
  value: string;
};

type ValidationErrorItem = {
  errorMessage?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isFile = (value: unknown): value is File => typeof File !== 'undefined' && value instanceof File;

const isValidationErrorItem = (value: unknown): value is ValidationErrorItem =>
  isRecord(value) && typeof value.errorMessage === 'string';

const getErrorMessageFromItems = (items: unknown): string | undefined =>
  Array.isArray(items) ? items.find(isValidationErrorItem)?.errorMessage : undefined;

export const hasFileValue = (value: unknown): boolean => Array.isArray(value) && value.some(isFile);

export const hasDocumentFiles = (data?: DocumentFormData): boolean => !!data && Object.values(data).some(hasFileValue);

export const createDocumentFormData = (data: DocumentFormData): FormData => {
  const formData = new FormData();
  const fields = Object.entries(data)
    .filter(([key]) => key !== 'file')
    .map(([key, value]) => ({ id: key, value }));

  formData.append('fields', new Blob([JSON.stringify({ fields })], { type: 'application/json' }));

  Object.values(data).forEach((value) => {
    if (!Array.isArray(value)) {
      return;
    }

    value.forEach((file) => {
      if (isFile(file)) {
        formData.append('file', file, file.name);
      }
    });
  });

  return formData;
};

export const getDynamicFormErrorMessage = (errorData: unknown): string | undefined => {
  const arrayError = getErrorMessageFromItems(errorData);

  if (arrayError) {
    return arrayError;
  }

  if (isRecord(errorData)) {
    return getErrorMessageFromItems(errorData.errors);
  }

  return undefined;
};

export const isSuccessfulDynamicFormStatus = (status: number): boolean =>
  DYNAMIC_FORM_SUCCESS_STATUSES.includes(status);

export const getOrchestrationId = (responseItems?: DynamicFormResponseItem[]): string | undefined =>
  responseItems?.find((item) => item.id === ORCHESTRATION_INSTANCE_ID)?.value;

export const submitDocumentForm = (endpoint: string, data: DocumentFormData) =>
  apiClient.post<DynamicFormResponseItem[]>(ApiType.external, {
    url: endpoint,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data: createDocumentFormData(data),
  });

export const validateDocumentForm = (data: DocumentFormData) =>
  apiClient.post(ApiType.apimFormApi, {
    url: ApiPaths[ApiType.apimFormApi].dynamicFormValidate,
    data: createDocumentFormData(data),
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    withCredentials: true,
  });
